import axios from 'axios'
import { closeModal } from '../../../../common/GlobalModal/redux/GlobalModalActions'
import { CPS, FINANCE, FUNDING, TRAILER_LEASING } from '../../../../constants/divisions'
import { notificationDanger, notificationSuccess, notificationWarning } from '../../../../utils/customToastify'
import {
  CLEAR_POSSIBLE_CONTACTS,
  CREDIT_APPLICATIONS_LOADING,
  GET_APPLICATION_PREVIEW,
  GET_CEF_BANK_REFERENCES,
  GET_CEF_LOANS,
  GET_CEF_OWNERS,
  GET_CEF_PAST_EMPLOYMENTS,
  GET_CEF_PERSONAL_REFERENCES,
  GET_CEF_RESIDENCES,
  GET_CREDIT_APPLICATION,
  GET_CREDIT_APPLICATIONS,
  GET_POSSIBLE_CONTACTS,
  ASSIGN_CREDIT_APPLICATIONS,
} from './applicationTypes'
import { mapDataForUpdate } from './mapper'

export const setLoading = (isLoading) => (dispatch) => {
  dispatch({ type: CREDIT_APPLICATIONS_LOADING, payload: isLoading })
}

const getBaseUrl = (divisionId) => {
  if ([FUNDING, TRAILER_LEASING].includes(divisionId)) {
    return '/website-credit-applications'
  } else if (divisionId === CPS) {
    return '/cps-credit-applications'
  } else if (divisionId === FINANCE) {
    return '/cef-credit-applications'
  }
  return ''
}

export const assignCreditApplications = ({ divisionId, applications, salespersonId }) => async (dispatch) => {
  try {
    dispatch(setLoading(true))
    const baseUrl = getBaseUrl(divisionId)
    if (baseUrl) {
      const response = await axios.post(`${baseUrl}/assign`, { applications, salespersonId })
      dispatch({ type: ASSIGN_CREDIT_APPLICATIONS, payload: response.data })
      notificationSuccess('Applications assigned successfully')
    }
  } catch (error) {
    notificationDanger('Failed to assign applications')
    notificationWarning(error.message)
  } finally {
    dispatch(setLoading(false))
  }
}

/**
 * fetch list
 */
export const getCreditApplications = (options) => async (dispatch) => {
  try {
    dispatch(setLoading(true))

    const url = getBaseUrl(options?.divisionId)

    if (url) {
      const res = await axios.get(url, { params: { ...options } })
      dispatch({ type: GET_CREDIT_APPLICATIONS, payload: res.data })
    }
    dispatch(setLoading(false))
  } catch (_) {
    dispatch(setLoading(false))
  }
}

/**
 * fetch one
 */
export const getCreditApplication = (id) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true))

    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    if (baseUrl) {
      const response = await axios.get(`${baseUrl}/${id}`)
      dispatch({ type: GET_CREDIT_APPLICATION, payload: response.data })
    }
    dispatch(setLoading(false))
  } catch (e) {
    dispatch(setLoading(false))
  }
}

export const getPossibleContacts = (options) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true))

    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    if (baseUrl) {
      const response = await axios.get(`${baseUrl}/possible-contacts`, { params: { ...options } })
      dispatch({ type: GET_POSSIBLE_CONTACTS, payload: response.data })
    }
    dispatch(setLoading(false))
  } catch (error) {
    dispatch(setLoading(false))
  }
}

export const clearPossibleContacts = () => (dispatch) => {
  dispatch({ type: CLEAR_POSSIBLE_CONTACTS })
}

export const mergeContact = (id, data, callback) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true))

    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    if (baseUrl) {
      await axios.patch(`${baseUrl}/merge-with-contact/${id}`, data)
      if (callback && typeof callback === 'function') {
        //fetch credit applications on success
        callback()
      }
    }
    dispatch(setLoading(false))
    dispatch(closeModal('possible_contacts_modal'))
  } catch (e) {
    dispatch(setLoading(false))
  }
}

export const createNewContact = (id, data, callback) => async (dispatch) => {
  try {
    dispatch(setLoading(true))
    if (!data?.organizationTypeId) {
      notificationDanger('Please Select Organization Type')
    } else {
      const existingContactRes = await axios.get('/contacts/existing', { params: { options: { ...data } } })

      const { data: existingContact } = existingContactRes

      // equal by mc# and dot# and name
      if (existingContact) {
        // merge
        dispatch(mergeContact(id, { contactId: existingContact.id }, callback))
      } else {
        // create new
        const response = await axios.post(`/contacts/credit-application-contact`, data)
        if (response.data?.id) {
          const data = { contactId: response.data.id }
          dispatch(mergeContact(id, data, callback))
        }
        dispatch(closeModal('possible_contacts_modal'))
      }
    }
  } finally {
    dispatch(setLoading(false))
  }
}

export const getApplicationPreview = (id) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true))

    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    if (baseUrl) {
      const response = await axios.get(`${baseUrl}/${id}/preview`)
      dispatch({ type: GET_APPLICATION_PREVIEW, payload: response.data })
    }
  } finally {
    dispatch(setLoading(false))
  }
}

export const completeCreditApplication = (id, data, options) => async (dispatch, getState) => {
  try {
    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    await axios.patch(`${baseUrl}/complete/${id}`, data)

    dispatch(getCreditApplications(options))
  } catch (_) {
    notificationDanger('Failed to complete application!')
  }
}

export const updateCreditApplication = (id, data) => async (_, getState) => {
  try {
    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    const d = mapDataForUpdate(data)
    await axios.patch(`${baseUrl}/${id}`, d)

    notificationSuccess('Application updated')
  } catch (_) {
    notificationDanger('Failed to update application!')
  }
}

export const getCefApplicationLoans = (id, options) => async (dispatch) => {
  dispatch(getApplicationData(id, GET_CEF_LOANS, 'loans', options))
}

export const getCefApplicationOwners = (id, options) => async (dispatch) => {
  dispatch(getApplicationData(id, GET_CEF_OWNERS, 'owners', options))
}

const getApplicationData = (id, type, urlFragment, options) => async (dispatch, getState) => {
  try {
    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)

    const res = await axios.get(`${baseUrl}/${urlFragment}/${id}`, { params: { ...options } })

    dispatch({ type, payload: res.data })
  } finally {
  }
}

export const getCefApplicantResidences = (id, options, isApplicant) => (dispatch) => {
  dispatch(getCefApplicantData(id, isApplicant, GET_CEF_RESIDENCES, 'residences', options))
}

export const getCefApplicantPastEmployments = (id, options, isApplicant) => (dispatch) => {
  dispatch(getCefApplicantData(id, isApplicant, GET_CEF_PAST_EMPLOYMENTS, 'past-employments', options))
}

export const getCefApplicantPersonalReferences = (id, options, isApplicant) => (dispatch) => {
  dispatch(getCefApplicantData(id, isApplicant, GET_CEF_PERSONAL_REFERENCES, 'personal-references', options))
}

export const getCefApplicantBankReferences = (id, options, isApplicant) => (dispatch) => {
  dispatch(getCefApplicantData(id, isApplicant, GET_CEF_BANK_REFERENCES, 'bank-references', options))
}

const getCefApplicantData = (id, isApplicant, type, urlFragment, options) => async (dispatch, getState) => {
  try {
    const state = getState()
    const divisionId = state.auth.user.division_id
    const baseUrl = getBaseUrl(divisionId)
    const res = await axios.get(`${baseUrl}/applicants/${id}/${urlFragment}`, { params: { ...options } })

    dispatch({ type, payload: res.data, isApplicant })
  } finally {
  }
}
